import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Truck, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { setupAPI } from '../api';

const Setup = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setupAPI.status()
      .then((res) => {
        if (!res.data.needs_setup) navigate('/login');
        else setChecking(false);
      })
      .catch(() => setChecking(false));
  }, [navigate]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await setupAPI.bootstrap({ name: form.name, email: form.email, password: form.password });
      await login(form.email, form.password);
      toast.success('Setup complete — welcome!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[var(--tms-bg)]"><Loader2 className="w-6 h-6 animate-spin text-[var(--tms-text-muted)]" /></div>;
  }

  return (
    <div className="min-h-screen flex bg-[var(--tms-bg)]">
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1657082874002-60069c7276ca?w=1200')` }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-bold mb-4">Welcome to Mila TMS</h2>
          <p className="text-lg text-slate-300">Let's set up your workspace. Create the first administrator account to get started.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg bg-[var(--tms-primary)] flex items-center justify-center shadow-lg">
              <Truck className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl text-[var(--tms-text)]" style={{ fontWeight: 900, letterSpacing: '-1px' }}>Mila<span style={{ color: 'var(--tms-primary)', fontWeight: 300 }}>TMS</span></h1>
              <p className="text-sm text-[var(--tms-text-muted)]">Initial setup — create your Super Admin</p>
            </div>
          </div>

          <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-6 text-sm text-[var(--tms-text-muted)]">
              <ShieldCheck className="w-4 h-4 text-[var(--tms-primary)]" />
              This account will have full access. Baseline data (accessorial codes) will be seeded automatically.
            </div>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="setup-form">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[var(--tms-text)]">Your Name</Label>
                <Input id="name" value={form.name} onChange={set('name')} required placeholder="Jane Doe"
                  className="bg-[var(--tms-bg)] border-[var(--tms-border)] text-[var(--tms-text)]" data-testid="setup-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[var(--tms-text)]">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={set('email')} required placeholder="you@company.com"
                  className="bg-[var(--tms-bg)] border-[var(--tms-border)] text-[var(--tms-text)]" data-testid="setup-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[var(--tms-text)]">Password</Label>
                <Input id="password" type="password" value={form.password} onChange={set('password')} required placeholder="••••••••"
                  className="bg-[var(--tms-bg)] border-[var(--tms-border)] text-[var(--tms-text)]" data-testid="setup-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-[var(--tms-text)]">Confirm Password</Label>
                <Input id="confirm" type="password" value={form.confirm} onChange={set('confirm')} required placeholder="••••••••"
                  className="bg-[var(--tms-bg)] border-[var(--tms-border)] text-[var(--tms-text)]" data-testid="setup-confirm-input" />
              </div>
              <Button type="submit" className="w-full bg-[var(--tms-primary)] hover:opacity-90 text-white shadow-lg" disabled={loading} data-testid="setup-submit-button">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Admin & Start
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setup;
